import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AimOutlined,
  AuditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DashboardOutlined,
  FileTextOutlined,
  InboxOutlined,
  PayCircleOutlined,
  ReadOutlined,
  SearchOutlined,
  ShopOutlined,
  TagsOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Layout, Menu, Typography } from "antd";
import type { MenuProps } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ADMIN_AUTH_SESSION_EVENT,
  clearAdminToken,
  getAdminToken,
  resetAdminAuthReconnectState,
} from "../services/api";
import ErrorBoundary from "../components/ErrorBoundary";
import AdminInboxBell from "../components/AdminInboxBell";
import ImpersonationControl from "../components/ImpersonationControl";
import StoreScopeSlot from "../components/StoreScopeSlot";
import AccountConnect from "./AccountConnect";
import AdminLoginWall from "./AdminLoginWall";
import { clearStoresCache, useStoresOptions } from "../hooks/useStoresOptions";
import {
  AdminScopeProvider,
  adminCanUseAnticheatClues,
  adminCanUseAiWeekly,
  adminCanUseDishCategory,
  adminCanUseFinanceStatements,
  adminCanUseProcurement,
  adminCanUseOwnerHome,
  adminCanUseInspections,
  adminCanUseInventoryTools,
  adminCanUsePerformance,
  adminCanUsePhotoCompliance,
  adminCanUseSalaryStandards,
  adminCanUseStoreProfile,
  adminCanUseStoreTools,
  adminCanUseWeeklyReports,
  adminHasGlobalScope,
  type AdminProfile,
  type AdminScopeStatus,
} from "./AdminScopeContext";
import { adminPeopleOpsMenuItemReadOnly } from "./adminEmployeeAccess";
import { adminCanUseCustomerVoice } from "./adminCustomerVoiceAccess";
import { NavigationGuardProvider } from "./NavigationGuardContext";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import { clearAllReviewDrafts } from "../pages/photo-compliance/reviewDraftStore";
import { clearAdminImpersonationSession } from "../services/impersonationSession";

const { Header, Sider, Content } = Layout;

type AdminMenuGroupKey = "biz" | "people" | "ops" | "master";

type AdminMenuItem = {
  key: string;
  icon: React.ReactNode;
  label: string;
  /** 所属分组 (侧栏四分组, 见 MENU_GROUPS)。 */
  group: AdminMenuGroupKey;
  disabled?: boolean;
};

/**
 * 侧栏四分组 (设计稿 frag-adm-e #ae-layout 批注①)。
 *
 * 改前: 12 项平铺进一条 Menu, "经营总览/绩效"与"库存/巡检/卫生"与"门店档案/品类/薪酬"
 * 混作一列, 扫读只能一行行找。改后按 经营 / 人事 / 运营 / 主数据配置 收拢;
 * 组内被角色过滤到 0 项时整组隐藏 (不留空组头)。
 */
const MENU_GROUPS: Array<{ key: AdminMenuGroupKey; label: string }> = [
  { key: "biz", label: "经营" },
  { key: "people", label: "人事" },
  { key: "ops", label: "运营" },
  { key: "master", label: "主数据配置" },
];

/**
 * 图标取 AntD Outlined 语义线性描边一族 (稿批注①; Ricky 拍板: 禁空心圆角方块占位 ——
 * 读起来像可打勾的复选框)。经营总览 Dashboard / 绩效 Aim / 员工 Team / 排班 Calendar /
 * 考勤 ClockCircle / 薪酬 PayCircle / 审批 Audit / 库存 Inbox / 巡检 Search /
 * 卫生 Camera / 门店 Shop / 品类 Tags。
 */
const menuItems: AdminMenuItem[] = [
  { key: "/", icon: <DashboardOutlined />, label: "经营总览", group: "biz" },
  { key: "/ai-weekly", icon: <ReadOutlined />, label: "AI 周报", group: "biz" },
  {
    key: "/weekly-report",
    icon: <FileTextOutlined />,
    label: "周报工作台",
    group: "biz",
  },
  {
    key: "/finance-statements",
    icon: <FileTextOutlined />,
    label: "月度财务报表",
    group: "biz",
  },
  {
    key: "/procurement",
    icon: <FileTextOutlined />,
    label: "采购管理",
    group: "biz",
  },
  // 绩效管理 (2026-06-04 月度目标 + 实时预估利润达成): owner / 区域 / 财务 / 超管.
  // 利润 / 毛利为「预估」(实时营收 × 成本模型), 月末财务结账后 true-up.
  {
    key: "/performance",
    icon: <AimOutlined />,
    label: "绩效管理",
    group: "biz",
  },
  // Ricky 2026-05-29 UX 简化: 员工入职已完全合并到「员工管理」页 Segmented tab,
  // 不再有独立 /employee-onboarding 路由 / 菜单项. 老链接由 App.tsx 路由
  // redirect 到 /employees?tab=invites (兼容书签 / 已分发的邀请详情 deep-link).
  // 顶部按钮「入职新员工」直接弹 modal (CreateOnboardingInviteModal), 不再跳页.
  {
    key: "/employees",
    icon: <TeamOutlined />,
    label: "员工管理",
    group: "people",
  },
  {
    key: "/scheduling",
    icon: <CalendarOutlined />,
    label: "排班管理",
    group: "people",
  },
  {
    key: "/attendance",
    icon: <ClockCircleOutlined />,
    label: "考勤管理",
    group: "people",
  },
  // v0.12.3 主线①: 薪酬标准录入 (写集 finance + super_admin — DEC-0022).
  // 仅写集角色可见 (见 roleAwareMenuItems 过滤), 非写集角色不展示该入口,
  // 避免出现"永久 disabled"的薪酬入口误导只读角色.
  {
    key: "/salary-standards",
    icon: <PayCircleOutlined />,
    label: "薪酬标准",
    group: "people",
  },
  {
    key: "/approvals",
    icon: <AuditOutlined />,
    label: "审批中心",
    group: "ops",
  },
  {
    key: "/customer-voice",
    icon: <CommentOutlined />,
    label: "桌访与客诉",
    group: "ops",
  },
  {
    key: "/inventory",
    icon: <InboxOutlined />,
    label: "库存管理",
    group: "ops",
  },
  {
    key: "/inspections",
    icon: <SearchOutlined />,
    label: "门店巡检",
    group: "ops",
  },
  {
    key: "/anticheat-clues",
    icon: <AimOutlined />,
    label: "防作弊线索",
    group: "ops",
  },
  // 门店档案 (2026-06-04 主数据基座): 店长维护门店经营属性 (面积/房租/类型/桌位/编制).
  // roleAware: 店长 / 区域 / 财务 / 老板 / 超管 (见 roleAwareMenuItems 过滤), 非维护集隐藏.
  {
    key: "/store-profile",
    icon: <ShopOutlined />,
    label: "门店配置",
    group: "master",
  },
  // 品类维护 (老板看板③消费点单 配套): 老板手动维护菜品↔品类归属.
  // 写集 = finance + owner + super_admin (adminCanUseDishCategory), 仅写集角色可见。
  {
    key: "/dish-category",
    icon: <TagsOutlined />,
    label: "品类维护",
    group: "master",
  },
];

/**
 * 只读标记键集合保留统一展示机制；V8 后 finance 的真实能力不再生成这些标签。
 */
const PEOPLE_OPS_READONLY_KEYS = new Set([
  "/employees",
  "/scheduling",
  "/attendance",
  "/approvals",
]);

const STORE_TOOL_KEYS = new Set(["/employees", "/scheduling", "/attendance"]);
const LAST_STORE_ID_KEY = "ember_admin_last_store_id";
const PROFILE_CACHE_KEY = "ember_admin_profile_cache_v1";
/** 全权限角色 (超管/owner) 的「查看门店」选择, 按 employeeId 隔离; sessionStorage —
 *  刷新不丢, 新开会话回到默认总览态 (2026-07-06 Ricky 拍板的默认态). */
const VIEW_STORE_KEY_PREFIX = "ember_admin_view_store_v1::emp";

function readViewStore(employeeId: number | null): number | null {
  if (employeeId === null) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(
      `${VIEW_STORE_KEY_PREFIX}${employeeId}`,
    );
    const storeId = raw ? Number(raw) : NaN;
    return Number.isInteger(storeId) && storeId > 0 ? storeId : null;
  } catch {
    return null;
  }
}

function writeViewStore(
  employeeId: number | null,
  storeId: number | null,
): void {
  if (employeeId === null) {
    return;
  }
  try {
    const key = `${VIEW_STORE_KEY_PREFIX}${employeeId}`;
    if (storeId === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, String(storeId));
    }
  } catch {
    // sessionStorage 不可用只失去刷新保持, 不影响本次切店.
  }
}

function cachedProfileIsValid(value: unknown): value is AdminProfile {
  if (!value || typeof value !== "object") {
    return false;
  }
  const profile = value as Partial<AdminProfile>;
  return (
    typeof profile.fullName === "string" &&
    typeof profile.positionCn === "string" &&
    typeof profile.role === "string" &&
    typeof profile.storeName === "string" &&
    (typeof profile.storeId === "number" || profile.storeId === null) &&
    typeof profile.isSuperAdmin === "boolean" &&
    (typeof profile.employeeId === "number" || profile.employeeId === null)
  );
}

function readCachedProfile(): AdminProfile | null {
  if (!getAdminToken()) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    return cachedProfileIsValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(profile: AdminProfile): void {
  try {
    window.sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
  } catch {
    // sessionStorage 不可用时只失去首屏稳定壳, 不影响真实权限校验。
  }
}

function clearCachedProfile(): void {
  try {
    window.sessionStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {
    // ignore
  }
}

function recentStoreQuery(): string {
  const raw = window.localStorage.getItem(LAST_STORE_ID_KEY);
  const storeId = raw ? Number(raw) : NaN;
  return Number.isInteger(storeId) && storeId > 0 ? `?storeId=${storeId}` : "";
}

function selectedKey(pathname: string): string {
  // DISC-040: 高亮判断与 pathnameBelongsToKey 口径统一, 用 `key + '/'` 分隔符,
  // 避免未来新增形如 `/inventory-report` 的顶层路由误高亮 `/inventory`。
  const match = menuItems
    .map((item) => item.key)
    .filter(
      (key) =>
        key !== "/" && (pathname === key || pathname.startsWith(`${key}/`)),
    )
    .sort((a, b) => b.length - a.length)[0];
  return match ?? "/";
}

/**
 * DISC-012: 当前 pathname 是否「真实」属于某个菜单 key 对应的路由。
 *
 * 与 selectedKey 的区别: selectedKey 对未匹配路由会 fallback 到首页 `/`,
 * 因此不能用 `nextKey === selectedKey(pathname)` 作为 short-circuit 依据 ——
 * 处于未匹配路由(fallback 到 `/`)时点「首页」会被误判成「已在该页」而 no-op,
 * 导致用户卡在未匹配路由逃不出去。
 *
 * 这里只在 pathname 确实落在该 key 对应路由时返回 true:
 * - 首页 `/`: 仅当 pathname 精确等于 `/`(或带 basename 后的根)才算真在首页;
 * - 其它 key: pathname 等于该 key 或以 `key + '/'` 开头才算真在该页。
 */
function pathnameBelongsToKey(pathname: string, key: string): boolean {
  if (key === "/") {
    return pathname === "/";
  }
  return pathname === key || pathname.startsWith(`${key}/`);
}

function routeAllowedForProfile(key: string, profile: AdminProfile): boolean {
  if (STORE_TOOL_KEYS.has(key)) {
    return adminCanUseStoreTools(profile);
  }
  if (key === "/inventory") {
    return adminCanUseInventoryTools(profile);
  }
  if (key === "/weekly-report") {
    return adminCanUseWeeklyReports(profile);
  }
  if (key === "/finance-statements") {
    return adminCanUseFinanceStatements(profile);
  }
  if (key === "/procurement") return adminCanUseProcurement(profile);
  if (key === "/customer-voice") {
    return adminCanUseCustomerVoice(profile);
  }
  if (key === "/inspections") {
    return adminCanUseInspections(profile);
  }
  if (key === "/salary-standards") {
    return adminCanUseSalaryStandards(profile);
  }
  if (key === "/store-profile") {
    return adminCanUseStoreProfile(profile);
  }
  if (key === "/performance") {
    return adminCanUsePerformance(profile);
  }
  if (key === "/dish-category") {
    return adminCanUseDishCategory(profile);
  }
  if (key === "/anticheat-clues") {
    return adminCanUseAnticheatClues(profile);
  }
  return true;
}

function anticheatMenuItemVisible(
  item: AdminMenuItem,
  profile: AdminProfile | null,
): boolean {
  return item.key !== "/anticheat-clues" || adminCanUseAnticheatClues(profile);
}

function isOwnerDashboardShell(
  ownerSidebarShell: boolean,
  pathname: string,
): boolean {
  return ownerSidebarShell && pathname === "/";
}

function canProfileSwitchStore(
  globalScope: boolean,
  profile: AdminProfile | null,
): boolean {
  return globalScope || profile?.role === "area_manager";
}

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeKey = useMemo(
    () => selectedKey(location.pathname),
    [location.pathname],
  );
  const [accountVersion, forceRefresh] = React.useReducer(
    (value: number) => value + 1,
    0,
  );
  const [scopeStatus, setScopeStatus] = useState<AdminScopeStatus>(
    getAdminToken() ? "checking" : "disconnected",
  );
  const [profile, setProfile] = useState<AdminProfile | null>(() =>
    readCachedProfile(),
  );
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [authReconnectSignal, setAuthReconnectSignal] = useState(0);
  const [postReconnectRouteCheckPending, setPostReconnectRouteCheckPending] =
    useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // 「查看门店」态 (2026-07-06): 全权限角色 (超管/owner) null=总览可切任意店;
  // 区经也可切 (GET /stores 后端已按名下门店裁剪), 但无总览态, 默认落档案挂靠店.
  const globalScope = adminHasGlobalScope(profile);
  const canSwitchStore = canProfileSwitchStore(globalScope, profile);
  const scopeReady = scopeStatus === "connected";
  const { options: headerStoreOptions } = useStoresOptions({
    enabled: scopeReady && canSwitchStore,
  });
  const [viewStoreId, setViewStoreIdState] = useState<number | null>(() =>
    readViewStore(profile?.employeeId ?? null),
  );
  const [siderCollapsed, setSiderCollapsed] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 1023px)");
    const sync = () => setSiderCollapsed(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  const profileEmployeeId = profile?.employeeId ?? null;
  useEffect(() => {
    // actor 切换 (含断开重连) 时按新 employeeId 恢复其会话内的查看门店选择.
    setViewStoreIdState(readViewStore(profileEmployeeId));
  }, [profileEmployeeId]);
  const setViewStoreId = useCallback(
    (next: number | null) => {
      setViewStoreIdState(next);
      writeViewStore(profileEmployeeId, next);
      if (next !== null) {
        try {
          window.localStorage.setItem(LAST_STORE_ID_KEY, String(next));
        } catch {
          // ignore
        }
      }
    },
    [profileEmployeeId],
  );
  const firstVisibleStoreId =
    headerStoreOptions.find((option) => option.disabled !== true)?.value ??
    null;
  const scopedDefaultStoreId =
    profile?.role === "area_manager"
      ? (profile.storeId ?? firstVisibleStoreId)
      : (profile?.storeId ?? null);
  const activeStoreId = canSwitchStore
    ? (viewStoreId ?? (globalScope ? null : scopedDefaultStoreId))
    : (profile?.storeId ?? null);
  // 防作弊页拥有独立的页内门店筛选。侧栏切店不能借 Content key 重挂页面、
  // 清掉用户正在查看的线索范围；其它消费 activeStoreId 的页面仍保持原重挂语义。
  const contentStoreScopeKey =
    ["/anticheat-clues", "/ai-weekly"].includes(location.pathname) ? "page-owned" : activeStoreId;
  // 2026-07-09 四角色对齐: 无店店长 (unsupported_scope) 不进驾驶舱壳, 走原引导。
  const ownerSidebarShell =
    scopeStatus !== "error" &&
    scopeStatus !== "unsupported_scope" &&
    adminCanUseOwnerHome(profile);
  const ownerDashboardShell = isOwnerDashboardShell(
    ownerSidebarShell,
    location.pathname,
  );
  const inventoryReady = adminCanUseInventoryTools(profile);
  const weeklyReportReady = adminCanUseWeeklyReports(profile);
  const financeStatementsReady = adminCanUseFinanceStatements(profile);
  const procurementReady = adminCanUseProcurement(profile);
  const customerVoiceReady = adminCanUseCustomerVoice(profile);
  const inspectionsReady = adminCanUseInspections(profile);
  // scopeLabel 已收口到 AccountConnect (DISC-NNN 顶部 6 块视觉收口), 此处删除避免双源漂移.
  const salaryStandardsReady = adminCanUseSalaryStandards(profile);
  const storeProfileReady = adminCanUseStoreProfile(profile);
  const performanceReady = adminCanUsePerformance(profile);
  const photoComplianceReady = adminCanUsePhotoCompliance(profile);
  const inspectionHubReady = inspectionsReady || photoComplianceReady;
  const dishCategoryReady = adminCanUseDishCategory(profile);
  const roleAwareMenuItems = useMemo<AdminMenuItem[]>(
    () =>
      menuItems
        // v0.12.3 主线①: 薪酬标准写集 = finance + super_admin (DEC-0022).
        // 仅写集角色 (已连接且可写) 展示该入口; 非写集 / 未连接一律隐藏,
        // 避免店长 / 上级 / 财务以外角色看到点了被后端 403 的薪酬入口.
        // 页面内仍有 scope gate 兜底 (defense-in-depth, 后端 RBAC 才是真兜底).
        .filter(
          (item) => item.key !== "/salary-standards" || salaryStandardsReady,
        )
        // 门店档案 / 绩效管理同范式 (2026-06-04): 仅维护集 / 可达集角色展示该入口,
        // 非可达角色隐藏 (页面内仍有 scope gate 兜底, 后端 RBAC 才是真兜底).
        .filter((item) => item.key !== "/store-profile" || storeProfileReady)
        .filter((item) => item.key !== "/performance" || performanceReady)
        .filter((item) => item.key !== "/weekly-report" || weeklyReportReady)
        .filter((item) => item.key !== "/ai-weekly" || adminCanUseAiWeekly(profile))
        .filter(
          (item) =>
            item.key !== "/finance-statements" || financeStatementsReady,
        )
        .filter((item) => item.key !== "/procurement" || procurementReady)
        .filter((item) => item.key !== "/customer-voice" || customerVoiceReady)
        .filter((item) => item.key !== "/inspections" || inspectionHubReady)
        .filter((item) => anticheatMenuItemVisible(item, profile))
        // 品类维护: finance / owner / super_admin 写集可见，其它角色隐藏入口.
        .filter((item) => item.key !== "/dish-category" || dishCategoryReady)
        .map((item) => {
          // 「（店长）」禁用后缀分支已删除 (2026-07-11, 稿批注④a 核实): 它挂在
          // disableStoreMenu = connected && !adminCanUseStoreTools 上, 而所有过不了
          // storeTools 的角色 (hq_ops / staff / unknown / 无店店长) 都被 isUnsupportedScope
          // 判成 unsupported_scope, 永远进不到 connected → 该分支永不触发, 且文案会误导
          // (店长根本不用 admin)。
          if (
            scopeStatus === "connected" &&
            item.key === "/inventory" &&
            !inventoryReady
          ) {
            return {
              ...item,
              disabled: true,
              label: `${item.label}（库存权限）`,
            };
          }
          if (
            scopeStatus === "connected" &&
            item.key === "/inspections" &&
            !inspectionHubReady
          ) {
            return {
              ...item,
              disabled: true,
              label: `${item.label}（巡检权限）`,
            };
          }
          return item;
        }),
    [
      dishCategoryReady,
      financeStatementsReady,
      procurementReady,
      weeklyReportReady,
      customerVoiceReady,
      inspectionHubReady,
      inventoryReady,
      performanceReady,
      profile,
      salaryStandardsReady,
      storeProfileReady,
      scopeStatus,
    ],
  );

  /**
   * 侧栏菜单 (AntD type:'group' 四分组)。过滤/禁用逻辑不动, 只在外面套一层分组;
   * 组内被过滤到 0 项 → 整组不出 (不留空组头)。
   *
   * 只读标记由统一能力函数派生；当前 finance 不再产生人事/审批只读标签。
   */
  const menuProps = useMemo<MenuProps["items"]>(() => {
    const decorate = (
      item: AdminMenuItem,
    ): Required<Pick<AdminMenuItem, "key">> & {
      icon: React.ReactNode;
      label: React.ReactNode;
      disabled?: boolean;
    } => {
      const readOnly =
        scopeStatus === "connected" &&
        PEOPLE_OPS_READONLY_KEYS.has(item.key) &&
        adminPeopleOpsMenuItemReadOnly(profile, item.key);
      return {
        key: item.key,
        icon: item.icon,
        disabled: item.disabled,
        label: readOnly ? (
          <span className="admin-menu-item">
            <span className="admin-menu-item__label">{item.label}</span>
            <span className="admin-menu-item__readonly">只读</span>
          </span>
        ) : (
          item.label
        ),
      };
    };

    return MENU_GROUPS.flatMap((group) => {
      const children = roleAwareMenuItems
        .filter((item) => item.group === group.key)
        .map(decorate);
      if (children.length === 0) {
        return [];
      }
      return [
        {
          key: `group:${group.key}`,
          type: "group" as const,
          label: group.label,
          children,
        },
      ];
    });
  }, [profile, roleAwareMenuItems, scopeStatus]);

  const handleScopeChange = useCallback(
    (next: {
      profile: AdminProfile | null;
      status: AdminScopeStatus;
      error?: string | null;
    }) => {
      setScopeStatus(next.status);
      setScopeError(next.status === "error" ? (next.error ?? null) : null);
      if (next.status === "checking") {
        if (next.profile) {
          setProfile(next.profile);
        }
        return;
      }
      setProfile(next.profile);
      if (next.status === "connected" && next.profile) {
        writeCachedProfile(next.profile);
      } else if (next.status === "disconnected" || next.status === "error") {
        clearCachedProfile();
      }
    },
    [],
  );

  /**
   * 登录墙成功回调: 票据已换出 JWT 并落盘, 这里把壳切回"校验中" —— 壳一挂,
   * AccountConnect 随之挂载并跑 getMe 范围校验, 状态机回到既有链路。
   */
  const handleWallAuthenticated = useCallback(() => {
    setScopeError(null);
    setScopeStatus("checking");
    forceRefresh();
  }, [forceRefresh]);

  /**
   * 退出登录 (壳级, 供顶栏 AccountConnect 与驾驶舱首页账号 chip 共用一条路径)。
   * DISC-147: 断开必清 stores cache, 否则下一个 actor 会读到上一个 actor 的 cached 全店。
   */
  const handleLogout = useCallback(() => {
    clearAllReviewDrafts();
    clearStoresCache();
    clearCachedProfile();
    clearAdminToken();
    clearAdminImpersonationSession();
    resetAdminAuthReconnectState();
    setProfile(null);
    setScopeError(null);
    setScopeStatus("disconnected");
    forceRefresh();
  }, [forceRefresh]);

  useEffect(() => {
    const handleAuthSessionEvent = () => {
      clearAllReviewDrafts();
      clearCachedProfile();
      clearStoresCache();
      clearAdminImpersonationSession();
      setProfile(null);
      setScopeStatus("disconnected");
      setPostReconnectRouteCheckPending(true);
      setAuthReconnectSignal((value) => value + 1);
    };

    window.addEventListener(ADMIN_AUTH_SESSION_EVENT, handleAuthSessionEvent);
    return () =>
      window.removeEventListener(
        ADMIN_AUTH_SESSION_EVENT,
        handleAuthSessionEvent,
      );
  }, []);

  useEffect(() => {
    if (!postReconnectRouteCheckPending) {
      return;
    }
    if (scopeStatus !== "connected" || !profile) {
      return;
    }
    setPostReconnectRouteCheckPending(false);
    const currentKey = selectedKey(location.pathname);
    if (!routeAllowedForProfile(currentKey, profile)) {
      navigate("/", { replace: true });
    }
  }, [
    location.pathname,
    navigate,
    postReconnectRouteCheckPending,
    profile,
    scopeStatus,
  ]);

  // DISC-012: 只有当前 pathname 真实属于 nextKey 对应路由时才 no-op;
  // 处于未匹配路由(selectedKey fallback 到首页)时仍要导航, 避免卡死逃不出去。
  //
  // D4 (DISC-039 关闭): 未保存守卫已整体上移到 useUnsavedChangesGuard 内的 useBlocker,
  // 不再在 handleMenuClick 内重复挂 modal.confirm — useBlocker 会在 navigate 触发时
  // 统一拦截 (含菜单 / 后退前进键 / 任意 <Link> / 任意 navigate(...)).
  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    const nextKey = String(key);
    if (pathnameBelongsToKey(location.pathname, nextKey)) {
      return;
    }
    // 全权限角色总览态不带 ?storeId= 兜底 — 落到页内「请选择查看门店」空态,
    // 门店由顶部切店器唯一决定, 避免 URL 残留与切店器显示打架 (2026-07-06).
    if (
      STORE_TOOL_KEYS.has(nextKey) &&
      activeStoreId === null &&
      !canSwitchStore
    ) {
      navigate(`${nextKey}${recentStoreQuery()}`);
      return;
    }
    navigate(nextKey);
  };

  // D4: useBlocker 接管应用内导航 (后退/前进/菜单/<Link>/navigate); beforeunload 仍单挂,
  // 守关页 / 刷新 / 地址栏外跳。两层互补不冲突。详见 useUnsavedChangesGuard 头注释。
  useUnsavedChangesGuard(hasUnsavedChanges);

  /**
   * scope gate (稿 frag-adm-e #ae-login 批注①): 断开 / 账号不可用时整屏就是登录墙,
   * 不再挂一个"12 项菜单都通向请先登录"的空壳后台。
   *
   * 只拦 disconnected / error 两态: checking 要保留骨架壳 (首屏稳定), unsupported_scope
   * 是"登进来了但这个身份不该用电脑端", 走页面内既有引导, 不该被登录墙覆盖。
   */
  if (scopeStatus === "disconnected" || scopeStatus === "error") {
    return (
      <AdminLoginWall
        status={scopeStatus}
        errorText={scopeError}
        onAuthenticated={handleWallAuthenticated}
      />
    );
  }

  return (
    <AdminScopeProvider
      value={{
        activeStoreId,
        profile,
        scopeReady,
        status: scopeStatus,
        canSwitchStore,
        setViewStoreId,
        logout: handleLogout,
      }}
    >
      <NavigationGuardProvider value={{ setHasUnsavedChanges }}>
        {/* 超管变身悬浮控件 (Ricky 2026-07-07): 独立于壳, 任何页面右上可用。
            驾驶舱壳不渲染浮层 (2026-07-09 重落 rc.112 丢失的 Codex 修复): 首页顶栏内
            有 inline 停靠实例 (HomeOwnerDashboardView), 浮层会与"本月"chip 滚动重叠。 */}
        {ownerDashboardShell ? null : (
          <ImpersonationControl isSuperAdmin={profile?.isSuperAdmin === true} />
        )}
        <Layout
          className={[
            "admin-shell",
            ownerDashboardShell ? "admin-shell--owner-dashboard" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Sider
            width="var(--admin-sider-width)"
            collapsedWidth="var(--admin-sider-collapsed-width)"
            collapsible
            breakpoint="lg"
            collapsed={siderCollapsed}
            trigger={null}
            className={
              ownerSidebarShell
                ? "admin-sider admin-sider--owner owner-dashboard-token-scope"
                : "admin-sider"
            }
            theme="light"
          >
            <div className="brand-block">
              <img src="/favicon.svg" alt="" className="brand-mark" />
              <div>
                <Typography.Text className="brand-title">
                  {ownerSidebarShell ? "星火 Ember" : "Ember · 星火"}
                </Typography.Text>
                <Typography.Text className="brand-subtitle">
                  {ownerSidebarShell ? "经营驾驶舱" : "餐饮管理电脑端"}
                </Typography.Text>
              </div>
            </div>
            {/* 全路由共用唯一门店作用域入口，页面消费 AdminScopeContext.activeStoreId。 */}
            <StoreScopeSlot
              profile={profile}
              ready={scopeReady}
              collapsed={siderCollapsed}
              canSwitchViewStore={canSwitchStore}
              globalScope={globalScope}
              activeStoreId={activeStoreId}
              storeOptions={headerStoreOptions}
              onViewStoreChange={setViewStoreId}
            />
            <Menu
              mode="inline"
              selectedKeys={[activeKey]}
              items={menuProps}
              onClick={handleMenuClick}
              className="admin-menu"
            />
          </Sider>
          <Layout>
            {/* 修复 (2026-06-04): AccountConnect 承载账号范围校验 (mount 时 getMe), 必须**始终挂载**。
                老板看板壳层 (ownerDashboardShell) 隐藏顶栏时改用 display:none 而非不渲染 ——
                否则 sessionStorage 缓存身份令 ownerDashboardShell 首帧即真 → Header 不挂载 →
                AccountConnect 不挂载 → 范围校验永不执行 → 卡在「账号范围校验中」(直接打开首页必现,
                切到「员工管理」再切回才解, rc.13 看板壳层回归)。视觉仍无顶栏 (display:none 不占位)。 */}
            <Header
              className="admin-header"
              style={ownerDashboardShell ? { display: "none" } : undefined}
            >
              <div className="admin-header-actions">
                <AdminInboxBell enabled={scopeStatus === "connected"} />
                <AccountConnect
                  reconnectSignal={authReconnectSignal}
                  ownerDashboard={ownerDashboardShell}
                  onTokenChange={forceRefresh}
                  onScopeChange={handleScopeChange}
                />
              </div>
            </Header>
            <Content
              className="admin-content"
              key={`${location.pathname}:${accountVersion}:${contentStoreScopeKey}:${scopeStatus}`}
            >
              <ErrorBoundary resetKey={location.pathname}>
                <Outlet />
              </ErrorBoundary>
            </Content>
          </Layout>
        </Layout>
      </NavigationGuardProvider>
    </AdminScopeProvider>
  );
};

export default AdminLayout;
